import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function formatMoneda(v) {
  if (v == null) return '$0';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

function formatFecha(f) {
  if (!f) return '-';
  return new Date(f).toLocaleDateString('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Genera PDF de cuenta de cobro
 * @param {Object} cuenta - Datos de la cuenta (de CUENTA_COBRO_DETALLE)
 */
export function generarCuentaCobroPDF(cuenta) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // === HEADER ===
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('CUENTA DE COBRO', pageWidth / 2, y, { align: 'center' });
  y += 8;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`No. ${cuenta.codigo}`, pageWidth / 2, y, { align: 'center' });
  y += 6;

  doc.setFontSize(9);
  doc.text(`Fecha de emisión: ${formatFecha(cuenta.fecha_emision)}`, pageWidth / 2, y, { align: 'center' });
  y += 10;

  // === DATOS DEL SOLDADO ===
  doc.setDrawColor(200, 200, 200);
  doc.line(15, y, pageWidth - 15, y);
  y += 6;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('DATOS DEL OCUPANTE', 15, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const datosOcupante = [
    ['Nombre:', cuenta.nombre_soldado],
    ['Documento:', `${cuenta.tipo_documento || ''} ${cuenta.numero_documento || ''}`],
    ['Grado:', cuenta.grado_militar_nombre || '-'],
    ['Ubicación:', `Habitación ${cuenta.habitacion_numero || '-'} / Cama ${cuenta.cama_numero || '-'}`],
  ];

  datosOcupante.forEach(([label, valor]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 15, y);
    doc.setFont('helvetica', 'normal');
    doc.text(valor, 55, y);
    y += 5;
  });

  y += 4;

  // === PERIODO ===
  doc.line(15, y, pageWidth - 15, y);
  y += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('PERIODO DE FACTURACIÓN', 15, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const datosPeriodo = [
    ['Periodo:', `${MESES[cuenta.periodo_mes - 1]} ${cuenta.periodo_anio}`],
    ['Desde:', formatFecha(cuenta.fecha_inicio_periodo)],
    ['Hasta:', formatFecha(cuenta.fecha_fin_periodo)],
    ['Días:', `${cuenta.dias_periodo}`],
  ];

  datosPeriodo.forEach(([label, valor]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 15, y);
    doc.setFont('helvetica', 'normal');
    doc.text(valor, 55, y);
    y += 5;
  });

  y += 4;

  // === DETALLE DE COBRO ===
  doc.line(15, y, pageWidth - 15, y);
  y += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('DETALLE', 15, y);
  y += 2;

  autoTable(doc, {
    startY: y,
    margin: { left: 15, right: 15 },
    head: [['Concepto', 'Días', 'Precio/Noche', 'Total']],
    body: [
      [
        `Hospedaje ${MESES[cuenta.periodo_mes - 1]} ${cuenta.periodo_anio}`,
        cuenta.dias_periodo,
        formatMoneda(cuenta.precio_noche),
        formatMoneda(cuenta.monto_total)
      ]
    ],
    theme: 'grid',
    headStyles: { fillColor: [59, 130, 246], fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { halign: 'center', cellWidth: 25 },
      2: { halign: 'right', cellWidth: 35 },
      3: { halign: 'right', cellWidth: 35 },
    },
  });

  y = doc.lastAutoTable.finalY + 8;

  // === RESUMEN DE MONTOS ===
  const resumenX = pageWidth - 80;
  doc.setFontSize(10);

  doc.setFont('helvetica', 'normal');
  doc.text('Total:', resumenX, y);
  doc.setFont('helvetica', 'bold');
  doc.text(formatMoneda(cuenta.monto_total), pageWidth - 15, y, { align: 'right' });
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.text('Pagado:', resumenX, y);
  doc.setTextColor(34, 197, 94);
  doc.setFont('helvetica', 'bold');
  doc.text(formatMoneda(cuenta.monto_pagado), pageWidth - 15, y, { align: 'right' });
  y += 6;

  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  doc.text('Saldo Pendiente:', resumenX, y);
  doc.setTextColor(239, 68, 68);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(formatMoneda(cuenta.saldo_pendiente), pageWidth - 15, y, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  y += 10;

  // === HISTORIAL DE PAGOS ===
  if (cuenta.pagos && cuenta.pagos.length > 0) {
    const pagosActivos = cuenta.pagos.filter(p => !p.anulado);
    if (pagosActivos.length > 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('PAGOS REGISTRADOS', 15, y);
      y += 2;

      autoTable(doc, {
        startY: y,
        margin: { left: 15, right: 15 },
        head: [['Código', 'Fecha', 'Método', 'Monto']],
        body: pagosActivos.map(p => [
          p.codigo,
          formatFecha(p.fecha_pago),
          p.metodo_pago,
          formatMoneda(p.monto)
        ]),
        theme: 'grid',
        headStyles: { fillColor: [34, 197, 94], fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          3: { halign: 'right' },
        },
      });

      y = doc.lastAutoTable.finalY + 8;
    }
  }

  // === ESTADO ===
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(128, 128, 128);
  const estadoText = `Estado: ${cuenta.estado.toUpperCase()}`;
  doc.text(estadoText, pageWidth / 2, y, { align: 'center' });

  if (cuenta.fecha_pago_completo) {
    y += 5;
    doc.text(`Pagado completamente el: ${formatFecha(cuenta.fecha_pago_completo)}`, pageWidth / 2, y, { align: 'center' });
  }

  // Abrir PDF en nueva pestaña
  const pdfBlob = doc.output('blob');
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank');
}
